import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authedFetch } from "@/lib/authedFetch";

function StatusMessage({ title, subtitle }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
      <div className="text-lg font-semibold mb-2">{title}</div>
      {subtitle ? <div className="text-sm text-gray-300 max-w-md text-center">{subtitle}</div> : null}
    </div>
  );
}

export default function StripeReturn({ mode = "return" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, authLoading, isAuthReady, refreshProfile } = useAuth() || {};

  const [status, setStatus] = useState("checking");
  const [error, setError] = useState(null);

  const returnToLogin = useMemo(() => {
    const dest = encodeURIComponent(location?.pathname + (location?.search || ""));
    return `/login?returnTo=${dest}`;
  }, [location?.pathname, location?.search]);

  const finalize = useCallback(async () => {
    setStatus("checking");
    setError(null);

    try {
      let res = await authedFetch("/api/stripe/connect-finalize", { method: "POST", body: JSON.stringify({}) });
      if (res.status === 404) {
        res = await authedFetch("/api/stripe/connect-finalise", { method: "POST", body: JSON.stringify({}) });
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        const message = json?.error || "Could not finalize Stripe connection.";
        setError(message);
        setStatus("error");
        return;
      }

      setStatus(json.connected ? "connected" : "pending");
      try {
        refreshProfile?.();
        window.dispatchEvent?.(new Event("mudo:profile-refresh"));
      } catch {}

      setTimeout(() => {
        navigate("/dashboard/settings?tab=billing", { replace: true });
      }, 400);
    } catch (err) {
      setError(err?.message || String(err));
      setStatus("error");
    }
  }, [navigate, refreshProfile]);

  useEffect(() => {
    if (authLoading || !isAuthReady) {
      setStatus("checking");
      return;
    }
    if (!session || !user) {
      setStatus("unauthenticated");
      navigate(returnToLogin, { replace: true });
      return;
    }
    finalize();
  }, [authLoading, isAuthReady, session, user, finalize, navigate, returnToLogin]);

  if (status === "checking") {
    return <StatusMessage title="Checking Stripe connection…" subtitle="Please wait a moment while we finalize your Stripe setup." />;
  }

  if (status === "connected") {
    return <StatusMessage title="Connected to Stripe" subtitle="Redirecting you to billing settings…" />;
  }

  if (status === "pending") {
    return <StatusMessage title="Almost there" subtitle="We updated your Stripe account. Redirecting you to billing settings…" />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-6 gap-4">
      <div className="text-lg font-semibold">Stripe connection failed</div>
      {error ? <div className="text-sm text-red-300 max-w-md text-center">{error}</div> : null}
      <div className="flex gap-3">
        <button
          onClick={finalize}
          className="px-4 py-2 rounded-md bg-white text-black font-semibold hover:bg-gray-200"
        >
          Try again
        </button>
        <button
          onClick={() => navigate("/dashboard/settings?tab=billing", { replace: true })}
          className="px-4 py-2 rounded-md bg-gray-800 text-white font-semibold hover:bg-gray-700"
        >
          Go to billing
        </button>
      </div>
    </div>
  );
}
